import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { correctionalFacilityId, geoData } from '@fs/zion-config'
import { useCanAddPersonalData } from '@fs/zion-permissions'

import { useFeatureFlag } from '@fs/zion-flags'
import { useUser } from '@fs/zion-user'
import { splunk } from '@fs/zion-splunk-logger'
import { usePrisonReportsPermission } from '../hooks/usePermission'
import uuid from '../utils/uuid'

export const AppContext = createContext({})
const key = 'give-clientId'

export const AppProvider = ({ children }) => {
  const [shareablePage, setShareablePage] = useState(false)
  const [skipLinkId, setSkipLinkId] = useState(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [canAddPersonalData] = useCanAddPersonalData()
  const { hasPermission, loading } = usePrisonReportsPermission()
  const clientId = window.localStorage.getItem(key) || uuid()
  const accessFromPrison = !!correctionalFacilityId && typeof correctionalFacilityId === 'string'
  const reportAccessFlag = useFeatureFlag('give_corrections_report_access')
  const prisonReportsPermissionOverrideFlag = useFeatureFlag('give_prison_reports_access_override')
  const prisonLoggingFlag = useFeatureFlag('give_corrections_logging')
  const { signedIn, userLoading } = useUser()

  const prisonReportsPermission = prisonReportsPermissionOverrideFlag.isOn || (hasPermission && reportAccessFlag.isOn)

  useEffect(() => {
    window.localStorage.setItem(key, clientId)
  }, [clientId])

  const value = useMemo(
    () => ({
      accessFromPrison,
      canAddPersonalData,
      clientId,
      correctionalFacilityId,
      geoCountry: geoData?.country,
      isFullscreen,
      prisonReportsPermission: {
        hasPermission: prisonReportsPermission,
        loading,
      },
      setFullscreen: setIsFullscreen,
      setShareablePage,
      setSkipLinkId,
      signedIn,
      shareablePage,
      skipLinkId,
      userLoading,
    }),
    [
      accessFromPrison,
      canAddPersonalData,
      clientId,
      isFullscreen,
      loading,
      prisonReportsPermission,
      shareablePage,
      signedIn,
      skipLinkId,
      userLoading,
    ]
  )

  useEffect(() => {
    if (prisonLoggingFlag.isOn && prisonLoggingFlag.config?.correctionalFacilityIdLogging) {
      splunk.log({
        message: 'app-provider:correctional-facility-id',
        correctionalFacilityId,
        accessFromPrison,
      })
    }
  }, [prisonLoggingFlag.isOn, prisonLoggingFlag.config?.correctionalFacilityIdLogging, accessFromPrison])

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export const useAppProvider = () => {
  return useContext(AppContext)
}
